import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface FilterPopoverOption<T extends string> {
  key: T;
  label: string;
  count: number;
  checked: boolean;
}

export interface FilterPopoverGroup<T extends string> {
  /** "Open" / "Closed" headings; omitted for a single flat list. */
  label?: string;
  options: FilterPopoverOption<T>[];
}

export interface FilterShortcut {
  label: string;
  onSelect: () => void;
}

interface FilterPopoverProps<T extends string> {
  /** "Service", "Event status", "Quote status". */
  label: string;
  /** The value after the colon: "All", "Open", "Drop-off +1"… */
  value: string;
  /** Off its default: the trigger gets the accent outline. */
  set: boolean;
  shortcuts: FilterShortcut[];
  groups: FilterPopoverGroup<T>[];
  onToggle: (key: T) => void;
}

/** One of the filter bar's dropdowns: shortcuts, then checkboxes with counts (#1557). */
export function FilterPopover<T extends string>({
  label,
  value,
  set,
  shortcuts,
  groups,
  onToggle,
}: FilterPopoverProps<T>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border bg-card px-[11px] py-1.5 text-[13.5px]",
            set && "border-accent shadow-[inset_0_0_0_1px_hsl(var(--accent))]",
          )}
        >
          {label}: <b className="font-bold">{value}</b>
          <span aria-hidden className="text-[11px] text-muted-foreground">
            ▾
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="grid w-[min(290px,calc(100vw-32px))] gap-1 rounded-[10px] bg-card p-2.5 text-sm shadow-[0_10px_30px_hsl(30_20%_10%/.18)]"
      >
        <div className="mb-0.5 flex flex-wrap gap-2.5 border-b px-1.5 pb-1.5 pt-0.5">
          {shortcuts.map((s) => (
            <button key={s.label} type="button" onClick={s.onSelect} className="p-0 text-[13px] font-bold text-accent">
              {s.label}
            </button>
          ))}
        </div>
        {groups.map((g) => (
          <div key={g.label ?? "options"} role="group" aria-label={g.label ?? label} className="grid gap-1">
            {g.label && (
              <h5 className="mx-1 mb-0.5 mt-1.5 text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">
                {g.label}
              </h5>
            )}
            {g.options.map((o) => (
              <label
                key={o.key}
                className="flex cursor-pointer items-center gap-[9px] rounded-md px-1.5 py-[5px] hover:bg-muted"
              >
                <Checkbox checked={o.checked} onCheckedChange={() => onToggle(o.key)} />
                {o.label}
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">{o.count}</span>
              </label>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
